import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { DropdownModule } from 'primeng/dropdown';
import { ClientsService } from '../../core/services/clients/clients.service';
import { faPlus, faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { faComment, faTrash, faXmark } from '@fortawesome/pro-light-svg-icons';
import { ContractObservationsService } from '../../core/services/contract-observations/contract-observations.service';
import { AuthService } from '../../core/services/auth.service';
import { PermissionService } from '../../core/services/permission.service';
import { UtcDatePipe } from '../../core/pipes/utc-date.pipe';
import { Rol } from '../../core/enums/rol.enum';

@Component({
  selector: 'app-comments-declarations',
  standalone: true,
  imports: [FaIconComponent, CommonModule, FormsModule, DropdownModule, UtcDatePipe],
  templateUrl: './comments-declarations.component.html',
  styleUrl: './comments-declarations.component.scss'
})
export class CommentsDeclarationsComponent implements OnInit {

  readonly clientService = inject(ClientsService);
  readonly contractObservationsService = inject(ContractObservationsService);
  readonly authService = inject(AuthService);
  readonly permissionService = inject(PermissionService);
  readonly isModalOpen = signal(false);

  faPlus = faPlus;
  faTrash = faTrash;
  faXmark = faXmark;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faComment = faComment;

  clients = signal<any[]>([]);
  selectedClient = signal<any>(null);
  contracts = signal<any[]>([]);
  selectedContract = signal<any>(null);
  comment = signal<string>('');
  isLoading = signal(false);
  contractObservations = signal<any[]>([]);
  showDeletedAvisos = signal(false);
  deletedAvisos = signal<any[]>([]);
  currentUserDepartment: string = '';

  ngOnInit() {
    this.loadClients();
    const currentUser = this.authService.getUser();
    this.currentUserDepartment = currentUser?.user?.department || '';
  }

  async loadClients() {
    try {
      this.isLoading.set(true);
      const clientsList = await this.clientService.getClientsNames();
      // Ordenar clientes alfabéticamente por nombre
      const sortedClients = (clientsList || []).sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return nameA.localeCompare(nameB);
      });
      this.clients.set(sortedClients);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  async onClientSelected(event: any) {
    const clientId = event.value;
    this.selectedClient.set(clientId);
    this.selectedContract.set(null);
    this.contracts.set([]);
    this.contractObservations.set([]);

    if (clientId) {
      try {
        this.isLoading.set(true);
        const contractsList = await this.clientService.getContractsByClient(clientId);
        // Ordenar contratos por nombre del modelo alfabéticamente
        const sortedContracts = (contractsList || []).sort((a, b) => {
          const nameA = a.model?.name || '';
          const nameB = b.model?.name || '';
          return nameA.localeCompare(nameB);
        });
        this.contracts.set(sortedContracts);
      } catch (error) {
        console.error('Error loading contacts:', error);
      } finally {
        this.isLoading.set(false);
      }
    }
  }

  async onContractSelected(event: any) {
    const contractId = event.value;
    this.selectedContract.set(contractId);
    this.contractObservations.set([]);
    this.deletedAvisos.set([]);
    this.showDeletedAvisos.set(false);

    if (contractId) {
      await this.loadContractObservations();
    }
  }

  async loadContractObservations() {
    if (!this.selectedContract()) {
      return;
    }

    try {
      this.isLoading.set(true);
      const observations = await this.contractObservationsService.getObservationsByContract(this.selectedContract());
      // Filter only notifications
      const notifications = (observations || []).filter((obs: any) => obs.isNotification === true);
      this.contractObservations.set(notifications);
    } catch (error) {
      console.error('Error loading contract observations:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  closeModal() {
    this.isModalOpen.set(false);
    this.resetForm();
  }

  resetForm() {
    this.selectedClient.set(null);
    this.selectedContract.set(null);
    this.comment.set('');
    this.contracts.set([]);
    this.contractObservations.set([]);
    this.deletedAvisos.set([]);
    this.showDeletedAvisos.set(false);
  }

  async addComment() {
    const userId = this.authService.getUserId();

    if (!userId || !this.comment()) {
      console.error('User ID or comment not found');
      return;
    }

    try {
      this.isLoading.set(true);
      await this.contractObservationsService.createObservation({
        userId: userId,
        contractId: this.selectedContract(),
        comment: this.comment(),
        isNotification: true
      });
      this.comment.set('');
      await this.loadContractObservations();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error al añadir el comentario. Por favor, inténtalo de nuevo.');
    } finally {
      this.isLoading.set(false);
    }
  }

  async deleteAviso(observation: any) {
    try {
      this.isLoading.set(true);
      await this.contractObservationsService.deleteObservation(observation.id);
      await this.loadContractObservations();
      if (this.showDeletedAvisos()) {
        await this.loadDeletedAvisos();
      }
    } catch (error) {
      console.error('Error deleting aviso:', error);
      alert('Error al eliminar el aviso. Por favor, inténtalo de nuevo.');
    } finally {
      this.isLoading.set(false);
    }
  }

  async toggleDeletedAvisos() {
    const next = !this.showDeletedAvisos();
    this.showDeletedAvisos.set(next);
    if (next && this.deletedAvisos().length === 0) {
      await this.loadDeletedAvisos();
    }
  }

  private async loadDeletedAvisos() {
    if (!this.selectedContract()) return;
    try {
      const log = await this.contractObservationsService.getObservationsByContractLog(this.selectedContract());
      this.deletedAvisos.set((log || []).filter((obs: any) => obs.deleted_at));
    } catch (error) {
      console.error('Error loading deleted avisos:', error);
    }
  }

  canDeleteAviso(observation: any): boolean {
    if (this.permissionService.hasRole(Rol.Admin)) {
      return true;
    }

    // Get the selected contract to check department
    const selectedContractData = this.contracts().find(c => c.id === this.selectedContract());
    if (!selectedContractData) {
      return false;
    }

    // Other users can only delete avisos if they belong to the same department as the model
    return this.currentUserDepartment === selectedContractData.model?.department;
  }

  saveObservation() {
    // Close modal - this is now just for closing, actual saving happens with addComment
    this.closeModal();
  }
}
